import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import {
  ArrowLeft,
  Globe,
  User as UserIcon,
  Settings,
  Trash2,
  Palette,
  Archive,
  ArchiveRestore,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { EditTenantDialog } from "@/components/tenants/edit-tenant-dialog";
import { DeleteTenantDialog } from "@/components/tenants/delete-tenant-dialog";
import { ArchiveTenantDialog } from "@/components/tenants/archive-tenant-dialog";
import { useTenantArchiveStatus } from "@/hooks/use-tenant-archive";
import { SsoSetupDialog } from "@/components/tenants/sso-setup-dialog";
import { LogoManagementDialog } from "@/components/tenants/logo-management-dialog";
import { PortalBrandingDialog } from "@/components/tenants/portal-branding-dialog";
import { HarmonyLogoIcon } from "@/components/icons/harmony-logo-icon";
import type { TenantRead } from "@harmonyso/tenants-sdk";
import { isRemoteCellId } from "@/lib/cells/cells";
import type { TenantBillingConfigResponse } from "@harmonyso/billing-api-sdk";

interface TenantHeaderProps {
  tenant: TenantRead;
  billingConfig?: TenantBillingConfigResponse;
}

export function TenantHeader({ tenant, billingConfig }: TenantHeaderProps) {
  const navigate = useNavigate();
  const [logoDialogOpen, setLogoDialogOpen] = useState(false);
  const [brandingDialogOpen, setBrandingDialogOpen] = useState(false);
  const canArchive = !isRemoteCellId(tenant.deployment_cell);
  const { data: archiveStatus } = useTenantArchiveStatus(tenant.id, canArchive);
  const isArchived = archiveStatus?.is_archived ?? false;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-4">
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            void navigate({ to: "/tenants" });
          }}
          className="hover:cursor-pointer"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Tenants
        </Button>
      </div>

      <div className="flex flex-row items-start justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => {
              setLogoDialogOpen(true);
            }}
            className="group relative h-20 w-20 cursor-pointer rounded-lg border transition-opacity hover:opacity-80"
            type="button"
          >
            {tenant.logo_url ? (
              <img
                src={tenant.logo_url}
                alt={`${tenant.company} logo`}
                className="h-full w-full rounded-lg object-contain p-2"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center rounded-lg">
                <HarmonyLogoIcon className="text-muted-foreground h-12 w-12" />
              </div>
            )}
          </button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              {tenant.company}
            </h1>
            <div className="text-muted-foreground mt-1 flex items-center space-x-2">
              <Globe className="h-4 w-4" />
              <span className="font-mono">{tenant.subdomain}</span>
            </div>
            <div className="mt-2 flex items-center gap-2">
              {tenant.industry && (
                <Badge variant="outline" className="text-xs">
                  {tenant.industry.charAt(0).toUpperCase() +
                    tenant.industry.slice(1)}
                </Badge>
              )}
              {tenant.size && (
                <Badge
                  variant="secondary"
                  className="flex items-center gap-1 text-xs"
                >
                  <UserIcon className="h-3 w-3" />
                  {tenant.size}
                </Badge>
              )}
              {billingConfig?.tier_name && (
                <Badge
                  variant="secondary"
                  className="bg-blue-600 text-xs text-white"
                >
                  {billingConfig.tier_name}
                </Badge>
              )}
              {isArchived && (
                <Badge
                  variant="secondary"
                  className="flex items-center gap-1 bg-amber-500 text-xs text-white"
                >
                  <Archive className="h-3 w-3" />
                  Archived
                </Badge>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setBrandingDialogOpen(true);
            }}
            className="hover:cursor-pointer"
          >
            <Palette className="mr-2 h-4 w-4" />
            Portal Branding
          </Button>
          <EditTenantDialog tenant={tenant} />
          <SsoSetupDialog tenant={tenant} />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="hover:cursor-pointer"
              >
                <Settings className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {canArchive && (
                <ArchiveTenantDialog
                  tenant={tenant}
                  isArchived={isArchived}
                  trigger={
                    <DropdownMenuItem
                      onSelect={(e) => {
                        e.preventDefault();
                      }}
                      className="cursor-pointer"
                    >
                      {isArchived ? (
                        <ArchiveRestore className="mr-2 h-4 w-4" />
                      ) : (
                        <Archive className="mr-2 h-4 w-4" />
                      )}
                      {isArchived ? "Unarchive Tenant" : "Archive Tenant"}
                    </DropdownMenuItem>
                  }
                />
              )}
              <DeleteTenantDialog
                tenant={tenant}
                trigger={
                  <DropdownMenuItem
                    onSelect={(e) => {
                      e.preventDefault();
                    }}
                    className="text-destructive focus:bg-destructive/10 focus:text-destructive cursor-pointer"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete Tenant
                  </DropdownMenuItem>
                }
              />
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <LogoManagementDialog
        tenant={tenant}
        open={logoDialogOpen}
        onOpenChange={setLogoDialogOpen}
      />

      <PortalBrandingDialog
        tenant={tenant}
        open={brandingDialogOpen}
        onOpenChange={setBrandingDialogOpen}
      />
    </div>
  );
}
